import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import AuthRoleSwitch from "../../components/AuthRoleSwitch/AuthRoleSwitch";
import { FormModal, SuccessModal } from "../../components/Modals";
import type { Field } from "../../components/Modals/FormModal/FormModal.types";
import {
  resetPassword,
  sendPasswordResetCode,
  verifyPasswordResetCode,
} from "../../services/authService/authService";
import type { AuthRole } from "../../types/auth";
import { useNotify } from "context/NotificationContext";
import { getServerErrorData } from "utils/httpError";
import { logger } from "utils/logger";

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState<AuthRole>(searchParams.get("role") === "student" ? "student" : "teacher");
  const [step, setStep] = useState<"email" | "code" | "newPassword">("email");
  const [email, setEmail] = useState("");
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const toast = useNotify();

  const fieldsByStep = useMemo<Record<"email" | "code" | "newPassword", Field[]>>(
    () => ({
      email: [
        {
          name: "email",
          type: "email",
          placeholder: "Электронная почта",
          required: true,
          width: "369px",
          validate: (value) => (!/\S+@\S+\.\S+/.test(value) ? "Неправильный формат почты" : null),
        },
      ],
      code: [
        { name: "resetCode", type: "text", placeholder: "Введите код", required: true, width: "369px" },
      ],
      newPassword: [
        {
          name: "password",
          type: "password",
          placeholder: "Пароль",
          required: true,
          width: "369px",
          validate: (value) => (value.length < 8 ? "Пароль должен содержать не менее 8 символов" : null),
        },
        {
          name: "confirmPassword",
          type: "password",
          placeholder: "Повторите пароль",
          required: true,
          width: "369px",
          validate: (value) => (value.length < 8 ? "Пароль должен содержать не менее 8 символов" : null),
        },
      ],
    }),
    [],
  );

  const footerLinks = [
    { text: "Войти в аккаунт", href: `/login?role=${role}`, variant: "primary" as const },
  ];

  const handleSubmit = async (formData: Record<string, string>) => {
    setIsLoading(true);
    setExternalErrors({});
    try {
      if (step === "email") {
        await sendPasswordResetCode(formData.email, "reset", role);
        setEmail(formData.email);
        setStep("code");
      } else if (step === "code") {
        await verifyPasswordResetCode(formData.resetCode, email, role);
        setStep("newPassword");
      } else {
        await resetPassword({
          email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          role,
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors: Record<string, string> = {};
        Object.entries(serverErrors).forEach(([key, value]) => {
          formattedErrors[key] = String(value);
        });
        setExternalErrors(formattedErrors);
      } else {
        logger.error("Ошибка восстановления пароля:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Произошла ошибка", life: 3000 });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const finish = () => {
    setShowSuccessModal(false);
    window.location.href = `/login?role=${role}`;
  };

  return (
    <>
      <FormModal
        title={step === "email" ? "Восстановление пароля" : step === "code" ? "Введите код" : "Новый пароль"}
        fields={fieldsByStep[step]}
        onSubmit={handleSubmit}
        footerLinks={footerLinks}
        submitButtonText={step === "email" ? "Отправить код" : "Подтвердить"}
        externalErrors={externalErrors}
        isLoading={isLoading}
        headerContent={
          <AuthRoleSwitch
            value={role}
            onChange={(nextRole) => step === "email" && setRole(nextRole)}
            disabled={step !== "email" || isLoading}
          />
        }
      />
      {showSuccessModal && <SuccessModal message="Ваш пароль успешно восстановлен!" onClose={finish} />}
    </>
  );
};

export default ResetPasswordPage;
